import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useTimeControlApi } from '../../hooks/useTimeControlApi';
import { getOptionLabel, getOptionValue, normalizeString, normalizeStringLower } from '../../utils/timeControlUtils';
import { fetchStageList, fetchStageResults } from '../../services/ResultsApi';
import RedFlagModal from '../common/RedFlagModal';
import SetupPopup from './SetupPopup';

function CompactSidePanel({
    devices = [],
    stages = [],
    sections = [],
    selectedStageId,
    selectedStageName = '',
    onDeviceClick,
    onStageChange,
    onStageSelect,
    onSectionSelect,
    selectedSectionName,
    modelColors = {},
    modelShowInProgressBar = {},
    clockOffset = 0,
    timeControlConfig = null,
    resultsConfig = null,
    currentMapId,
    onMapChange,
    isReadOnly = false,
}) {
    const [isCarStagesVisible, setIsCarStagesVisible] = useState(true);
    const [isProgressVisible, setIsProgressVisible] = useState(true);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [stageName, setStageName] = useState(selectedStageName);
    const [activeProgressTab, setActiveProgressTab] = useState('timeControl'); // 'analytics', 'timeControl', 'results'
    const [activeCarStagesTab, setActiveCarStagesTab] = useState('cars'); // 'cars', 'stage'
    const [isRedFlagModalOpen, setIsRedFlagModalOpen] = useState(false);
    const [isSetupPopupOpen, setIsSetupPopupOpen] = useState(false);

    // Results API state
    const [resultStages, setResultStages] = useState([]);
    const [selectedResultStageId, setSelectedResultStageId] = useState('');
    const [stageResults, setStageResults] = useState([]);
    const [resultsLoading, setResultsLoading] = useState(false);
    const [resultsError, setResultsError] = useState(null);
    const [resultsManuallySelected, setResultsManuallySelected] = useState(false);
    const isMountedRef = useRef(true);
    const selectedResultStageRef = useRef('');

    const competitionId = resultsConfig?.competitionId;
    const websocketUrl = resultsConfig?.websocketUrl;

    // Time Control API integration
    const {
        rallyOptions,
        rallyLoading,
        rallyError,
        selectedRally,
        setSelectedRally,
        rallyPoints,
        pointsLoading,
        pointsError,
        selectedRallyPoint,
        setSelectedRallyPoint,
        pointTimes,
        timesLoading,
        timesError,
    } = useTimeControlApi(timeControlConfig);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Mobile detection
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isMobileCollapsed, setIsMobileCollapsed] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile) {
                setIsMobileCollapsed(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleMobileExpand = (view) => {
        setIsMobileCollapsed(false);
        if (view === 'cars') {
            setIsCarStagesVisible(true);
            setIsProgressVisible(false);
        } else if (view === 'progress') {
            setIsCarStagesVisible(false);
            setIsProgressVisible(true);
        }
    };

    // --- Results API Integration ---

    const refreshResultStages = useCallback(async () => {
        if (!competitionId) {
            if (isMountedRef.current) {
                setResultStages([]);
                setSelectedResultStageId('');
            }
            return [];
        }

        const list = await fetchStageList(competitionId);
        const safeList = Array.isArray(list) ? list : [];

        if (isMountedRef.current) {
            setResultStages(safeList);
        }

        return safeList;
    }, [competitionId]);

    const refreshStageResults = useCallback(async (stageId) => {
        if (!stageId) {
            if (isMountedRef.current) {
                setStageResults([]);
                setResultsLoading(false);
            }
            return;
        }

        if (isMountedRef.current) {
            setResultsLoading(true);
            setResultsError(null);
        }

        try {
            console.log('CompactSidePanel: fetching results for stageId:', stageId);
            const data = await fetchStageResults(stageId);
            const safeData = Array.isArray(data) ? data : [];
            if (isMountedRef.current) {
                setStageResults(safeData);
            }
        } catch (error) {
            console.error('CompactSidePanel: error fetching results:', error);
            if (isMountedRef.current) {
                setStageResults([]);
                setResultsError('Unable to load results at this time.');
            }
        } finally {
            if (isMountedRef.current) {
                setResultsLoading(false);
            }
        }
    }, []);

    // Initial load of stage list
    useEffect(() => {
        refreshResultStages();
    }, [refreshResultStages]);

    // WebSocket listener for updates
    useEffect(() => {
        if (!websocketUrl) return undefined;

        const ws = new WebSocket(websocketUrl);
        ws.onmessage = async () => {
            console.log('CompactSidePanel: WebSocket message received, refreshing...');
            await refreshResultStages();
            const currentStage = selectedResultStageRef.current;
            if (currentStage) {
                refreshStageResults(currentStage);
            }
        };

        return () => {
            ws.close();
        };
    }, [websocketUrl, refreshResultStages, refreshStageResults]);

    // Sync ref
    useEffect(() => {
        selectedResultStageRef.current = selectedResultStageId;
    }, [selectedResultStageId]);

    // Fetch results when ID changes
    useEffect(() => {
        if (!selectedResultStageId) return;
        refreshStageResults(selectedResultStageId);
    }, [selectedResultStageId, refreshStageResults]);

    // Auto-select stage based on route name (if not manually overridden)
    useEffect(() => {
        setResultsManuallySelected(false);
    }, [stageName]);

    useEffect(() => {
        if (!Array.isArray(resultStages) || resultStages.length === 0) {
            // Only clear if we really have no stages to show? 
            // Better to keep previous selection if possible or just do nothing.
            // But if list is empty, we can't select anything valid.
            if (resultStages.length === 0 && selectedResultStageId) {
                // optionally clear or leave as is
            }
            return;
        }

        const normalizedStageName = stageName ? normalizeStringLower(stageName) : '';
        const matchByName = normalizedStageName
            ? resultStages.find(
                (stage) => stage?.Name && normalizeStringLower(stage.Name) === normalizedStageName
            )
            : null;

        setSelectedResultStageId((prev) => {
            if (matchByName && (!prev || (!resultsManuallySelected && prev !== String(matchByName.ID)))) {
                return String(matchByName.ID);
            }
            // If nothing selected, default to first?
            if (!prev) {
                const first = resultStages[0];
                if (first?.ID != null) {
                    return String(first.ID);
                }
            }
            return prev;
        });
    }, [resultStages, stageName, resultsManuallySelected]);

    // --- End Results API Integration ---


    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        setStageName(selectedStageName);
    }, [selectedStageName]);

    const adjustedTime = new Date(currentTime.getTime() + clockOffset * 1000);
    const formatTime = (date) => {
        const h = date.getHours().toString().padStart(2, '0');
        const m = date.getMinutes().toString().padStart(2, '0');
        const s = date.getSeconds().toString().padStart(2, '0');
        return `${h}:${m}:${s}`;
    };

    // Sort devices: letter-prefixed names first (S1, S2, Os…) then numeric (1, 2, 3…)
    const sortedDevices = useMemo(() => {
        const arr = [...devices];
        return arr.sort((a, b) => {
            const aName = a.deviceName ? a.deviceName.split('-')[0].trim() : 'Unknown';
            const bName = b.deviceName ? b.deviceName.split('-')[0].trim() : 'Unknown';
            const aHasLetter = /^[a-zA-Z]/.test(aName);
            const bHasLetter = /^[a-zA-Z]/.test(bName);
            if (aHasLetter && !bHasLetter) return -1;
            if (!aHasLetter && bHasLetter) return 1;
            if (aHasLetter && bHasLetter) return aName.localeCompare(bName);
            const numA = parseInt(aName, 10);
            const numB = parseInt(bName, 10);
            if (isNaN(numA) && isNaN(numB)) return 0;
            if (isNaN(numA)) return 1;
            if (isNaN(numB)) return -1;
            return numA - numB;
        });
    }, [devices]);

    // Get device color based on status
    // NOTE: after TraccarApiPoller fix, status fields are in device.attributes
    const SOS_COLOR      = '#dc2626'; // hard red — SOS only
    const REDFLAG_COLOR  = '#9333ea'; // violet — red flag sent/confirmed
    const warningColor   = modelColors['Warning'] || '#f9d300';
    const defaultColor   = modelColors[''] || '#0095d5';

    const getDeviceColor = (d) => {
        const attrs = d.attributes || {};
        const isSos      = d.isSosActive === true || attrs.CarStatus === 'SOS';
        const isRedFlag  = attrs.CarStatus === 'REDFLAG' || attrs.CarStatus === 'REDFLAG_SEND' || attrs.CarStatus === 'REDFLAG_CONFIRM' || attrs.redflag === true;
        const isOk       = attrs.CarStatus === 'OK';

        if (isSos)     return SOS_COLOR;     // 🔴 red for SOS
        if (isRedFlag) return REDFLAG_COLOR; // 🟣 violet for red flag
        if (isOk)      return 'green';       // 🟢 explicit OK from SMS only
        const model = d.deviceModel || d.model;
        return modelColors[model] || defaultColor;
    };

    // Get Tailwind color class based on device color
    const getDeviceColorClass = (d) => {
        const color = getDeviceColor(d);
        if (color === SOS_COLOR)     return 'bg-red-600';
        if (color === REDFLAG_COLOR) return 'bg-purple-600';
        if (color === warningColor)  return 'bg-yellow-400';
        if (color === 'green')       return 'bg-emerald-500';
        if (color === defaultColor)  return 'bg-blue-600';
        if (color.includes('orange') || color.includes('#ff')) return 'bg-orange-500';
        if (color.includes('gray')   || color.includes('grey')) return 'bg-slate-500';
        return 'bg-blue-600';
    };

    // Get selected stage
    const selectedStage = stages.find((s) => s.name === stageName);
    const stageLengthKm = selectedStage?.routeIndex?.cum
        ? selectedStage.routeIndex.cum[selectedStage.routeIndex.cum.length - 1] / 1000
        : 1.3; // default from example

    // Filter devices on the selected stage
    const devicesOnStage = useMemo(() => {
        return devices.filter((d) => {
            const km = Number.parseFloat(d.distanceOnPolyline);
            const modelName = d.deviceModel || d.model;
            // If the flag is explicitly false, hide from progress bar; default to showing
            const showInBar = modelShowInProgressBar[modelName] !== false;
            return (
                showInBar &&
                d.onStage &&
                d.routeName === stageName &&
                Number.isFinite(km) &&
                km >= 0.025  // hide until car is >25m from start line
            );
        }).sort((a, b) => {
            const kmA = Number.parseFloat(a.distanceOnPolyline);
            const kmB = Number.parseFloat(b.distanceOnPolyline);
            if (!Number.isFinite(kmA) && !Number.isFinite(kmB)) return 0;
            if (!Number.isFinite(kmA)) return 1;
            if (!Number.isFinite(kmB)) return -1;
            return kmA - kmB;
        });
    }, [devices, stageName, modelShowInProgressBar]);

    // --- Animated rows for smooth enter/exit in progress bar ---
    const [animatedRows, setAnimatedRows] = useState([]);
    const leaveTimersRef = useRef({});

    useEffect(() => {
        const incomingIds = new Set(devicesOnStage.map(d => String(d.deviceId ?? d.id)));

        setAnimatedRows(prev => {
            const prevIds = new Set(prev.map(r => r.rowId));

            // 1. Mark rows no longer in devicesOnStage as 'leaving'
            const next = prev.map(r => {
                if (!incomingIds.has(r.rowId) && r.phase !== 'leaving') {
                    // Schedule removal after exit animation
                    if (!leaveTimersRef.current[r.rowId]) {
                        leaveTimersRef.current[r.rowId] = setTimeout(() => {
                            setAnimatedRows(p => p.filter(x => x.rowId !== r.rowId));
                            delete leaveTimersRef.current[r.rowId];
                        }, 320);
                    }
                    return { ...r, phase: 'leaving' };
                }
                return r;
            });

            // 2. Add new rows with 'entering' phase; update data for existing ones
            devicesOnStage.forEach(d => {
                const rid = String(d.deviceId ?? d.id);
                const existingIdx = next.findIndex(r => r.rowId === rid);
                if (existingIdx === -1) {
                    // Cancel any pending leave timer if device came back
                    if (leaveTimersRef.current[rid]) {
                        clearTimeout(leaveTimersRef.current[rid]);
                        delete leaveTimersRef.current[rid];
                    }
                    next.push({ rowId: rid, data: d, phase: 'entering' });
                } else {
                    // Keep phase but refresh data
                    next[existingIdx] = { ...next[existingIdx], data: d };
                }
            });

            // Sort by km so the list stays 0 → finish-line order
            next.sort((a, b) => {
                const kmA = Number.parseFloat(a.data?.distanceOnPolyline);
                const kmB = Number.parseFloat(b.data?.distanceOnPolyline);
                if (!Number.isFinite(kmA) && !Number.isFinite(kmB)) return 0;
                if (!Number.isFinite(kmA)) return 1;
                if (!Number.isFinite(kmB)) return -1;
                return kmA - kmB;
            });

            return next;
        });
    }, [devicesOnStage]);

    // Promote 'entering' rows to 'visible' after animation (350ms)
    useEffect(() => {
        const enteringRows = animatedRows.filter(r => r.phase === 'entering');
        if (enteringRows.length === 0) return;
        const t = setTimeout(() => {
            setAnimatedRows(prev =>
                prev.map(r => r.phase === 'entering' ? { ...r, phase: 'visible' } : r)
            );
        }, 360);
        return () => clearTimeout(t);
    }, [animatedRows]);

    // Cleanup leave timers on unmount
    useEffect(() => {
        return () => {
            Object.values(leaveTimersRef.current).forEach(clearTimeout);
        };
    }, []);

    // Get progress bar color based on percentage
    const getProgressColor = (pct) => {
        if (pct < 20) return 'bg-rose-500';
        if (pct < 60) return 'bg-orange-500';
        return 'bg-emerald-500';
    };

    // Get all available stages for dropdown
    const allStages = useMemo(() => {
        const stageList = [];
        sections.forEach((sec) => {
            if (sec.stages && Array.isArray(sec.stages)) {
                sec.stages.forEach((st) => {
                    stageList.push({ ...st, section: sec.section });
                });
            }
        });
        return stageList;
    }, [sections]);

    // Normalize time control data from API
    const normalizeTimeControlEntry = (entry, fallbackIndex = 0) => {
        if (entry == null) return null;
        if (typeof entry === 'string' || typeof entry === 'number') {
            return {
                number: fallbackIndex + 1,
                time: String(entry),
            };
        }
        if (typeof entry !== 'object') return null;

        const pick = (...keys) => {
            for (const key of keys) {
                const value = entry[key];
                if (value !== undefined && value !== null && value !== '') {
                    return value;
                }
            }
            return undefined;
        };

        const numberValue = pick(
            'number', 'startNumber', 'start_number', 'startNo', 'start_no',
            'carNumber', 'car_number', 'carNo', 'car_no', 'vehicleNumber',
            'vehicle_number', 'competitorNumber', 'competitor_number',
            'teamNumber', 'team_number', 'crewNumber', 'crew_number',
            'position', 'pos', 'rank', 'no', 'id', 'name', 'label'
        );

        const timeValue = pick(
            'time', 'timestamp', 'timeValue', 'time_value', 'value',
            'recordedAt', 'recorded_at', 'result', 'duration', 'split',
            'splitTime', 'split_time', 'display', 'text', 'timeFormatted',
            'time_formatted'
        );

        return {
            number: numberValue ?? fallbackIndex + 1,
            time: timeValue != null ? String(timeValue) : '',
        };
    };

    const normalizedPointTimes = useMemo(() => {
        if (!Array.isArray(pointTimes) || pointTimes.length === 0) {
            return [];
        }
        return pointTimes
            .map((entry, idx) => normalizeTimeControlEntry(entry, idx))
            .filter(Boolean);
    }, [pointTimes]);

    // Get results data from devices on stage
    const resultsData = devicesOnStage.map((d) => {
        const id = d.deviceId ?? d.id;
        const name = d.deviceName ? d.deviceName.split('-')[0].trim() : id;
        const number = parseInt(name, 10);

        // Check if device has finished (you can adjust this logic based on your data structure)
        const hasFinished = d.finishTime || d.status === 'finished';
        const isOngoing = d.onStage && !hasFinished;

        return {
            number: isNaN(number) ? name : number,
            start: d.startTime || '00:00:00.0',
            finish: d.finishTime || null,
            result: d.resultTime || d.elapsedTime || null,
            status: isOngoing ? 'ongoing' : 'finished',
            deviceId: id
        };
    });


    const MobileIconStrip = () => (
        <div className="fixed right-4 top-24 flex flex-col gap-4 z-[1100] pointer-events-auto">
            <button
                onClick={() => handleMobileExpand('cars')}
                className="w-12 h-12 bg-white rounded-2xl shadow-lg flex items-center justify-center text-blue-600 transition-transform active:scale-95"
            >
                <span className="material-symbols-outlined text-2xl">directions_car</span>
            </button>
            <button
                onClick={() => handleMobileExpand('progress')}
                className="w-12 h-12 bg-white rounded-2xl shadow-lg flex items-center justify-center text-blue-600 transition-transform active:scale-95"
            >
                <span className="material-symbols-outlined text-2xl">analytics</span>
            </button>
            <div className="h-4"></div> {/* Spacer */}
            {!isReadOnly && (
                <button
                    onClick={() => setIsRedFlagModalOpen(true)}
                    className="w-12 h-12 bg-rose-600 rounded-2xl shadow-lg flex items-center justify-center text-white transition-transform active:scale-95 animate-pulse"
                >
                    <span className="material-symbols-outlined text-2xl">flag</span>
                </button>
            )}
        </div>
    );

    if (isMobile && isMobileCollapsed) {
        return (
            <>
                <MobileIconStrip />
                <RedFlagModal
                    isOpen={isRedFlagModalOpen}
                    onClose={() => setIsRedFlagModalOpen(false)}
                    stages={allStages}
                    devices={devices}
                    initialStageName={stageName}
                />
            </>
        );
    }

    return (
        <aside className={`w-full max-w-[340px] flex flex-col gap-3 font-display pointer-events-none transition-all ${isProgressVisible ? 'h-[95vh]' : 'h-auto max-h-[95vh]'} ${isMobile ? 'fixed right-0 top-0 h-screen z-[1100] bg-slate-100/90 backdrop-blur-sm p-4' : ''}`}>
            {isMobile && (
                <button
                    onClick={() => setIsMobileCollapsed(true)}
                    className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-sm z-50 pointer-events-auto text-slate-500"
                >
                    <span className="material-symbols-outlined">close</span>
                </button>
            )}
            {/* Cars & Stages Panel */}
            <div className="glass-panel rounded-2xl shadow-xl overflow-hidden shrink-0 pointer-events-auto">
                <header className="px-3 py-2 flex items-center justify-between border-b border-black/5">
                    <div className="flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-primary text-lg">directions_car</span>
                        <h2 className="font-extrabold text-slate-800 uppercase tracking-tight text-[10px]">
                            Cars &amp; Stages
                        </h2>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="font-mono text-[11px] font-bold text-emerald-600">
                            {formatTime(adjustedTime)}
                        </span>

                        <span
                            className="material-symbols-outlined text-slate-400 text-sm cursor-pointer hover:text-slate-600 transition-colors"
                            onClick={() => setIsSetupPopupOpen(true)}
                            title="Setup"
                        >
                            settings
                        </span>
                        <span
                            className="material-symbols-outlined text-slate-400 text-base cursor-pointer"
                            onClick={() => setIsCarStagesVisible(!isCarStagesVisible)}
                        >
                            {isCarStagesVisible ? 'expand_less' : 'expand_more'}
                        </span>
                    </div>
                </header>

                {isCarStagesVisible && (
                    <>
                        {/* Tab Navigation */}
                        <div className="flex border-b border-black/5">
                            <button
                                onClick={() => setActiveCarStagesTab('cars')}
                                className={`flex-1 py-1.5 text-[10px] flex items-center justify-center gap-1.5 border-b-2 transition-colors ${activeCarStagesTab === 'cars'
                                    ? 'border-primary text-primary font-bold'
                                    : 'border-transparent text-slate-500 hover:text-slate-700'
                                    }`}
                            >
                                <span className="material-symbols-outlined text-sm">directions_car</span> CARS
                            </button>
                            <button
                                onClick={() => setActiveCarStagesTab('stage')}
                                className={`flex-1 py-1.5 text-[10px] flex items-center justify-center gap-1.5 border-b-2 transition-colors ${activeCarStagesTab === 'stage'
                                    ? 'border-primary text-primary font-bold'
                                    : 'border-transparent text-slate-500 hover:text-slate-700'
                                    }`}
                            >
                                <span className="material-symbols-outlined text-sm">map</span> STAGE
                            </button>
                        </div>

                        {/* CARS Tab Content */}
                        {activeCarStagesTab === 'cars' && (
                            <div
                                className="p-3 gap-1.5 max-h-[400px] overflow-y-auto custom-scrollbar"
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(7, 1fr)'
                                }}
                            >
                                {sortedDevices.map((device, i) => {
                                    const deviceName = device.deviceName ? device.deviceName.split('-')[0].trim() : 'Unknown';
                                    if (deviceName.toLowerCase().startsWith('unknown')) return null;

                                    const hexColor = getDeviceColor(device);
                                    const attrs = device.attributes || {};
                                    const isRedFlag = attrs.CarStatus === 'REDFLAG' || attrs.CarStatus === 'REDFLAG_SEND' || attrs.CarStatus === 'REDFLAG_CONFIRM' || attrs.redflag === true;

                                    const model = device.deviceModel || device.model || '';
                                    const useDarkText = model.toLowerCase() === 'nie aktywny';
                                    return (
                                        <div
                                            key={i}
                                            className={`${useDarkText ? 'text-black' : 'text-white'} h-7 text-[11px] flex items-center justify-center rounded font-bold shadow-sm cursor-pointer hover:opacity-80 hover:scale-105 transition-all overflow-hidden text-ellipsis whitespace-nowrap relative`}
                                            style={{
                                                backgroundColor: hexColor,
                                                opacity: device.isStale ? 0.4 : 1,
                                                filter: device.isStale ? 'grayscale(60%)' : 'none',
                                            }}
                                            onClick={() => onDeviceClick(device.latitude, device.longitude, device.deviceId ?? device.id)}
                                            title={device.isStale ? `${device.deviceName || deviceName} — disconnected` : (device.deviceName || deviceName)}
                                        >
                                            {deviceName}
                                            {isRedFlag && !device.isStale && (
                                                <span className="material-symbols-outlined absolute right-0.5 text-[10px] animate-pulse">flag</span>
                                            )}
                                            {device.isStale && (
                                                <span className="material-symbols-outlined absolute right-0.5 text-[10px]">signal_wifi_off</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* STAGE Tab Content - Leg-based selector */}
                        {activeCarStagesTab === 'stage' && (
                            <div className="p-2 space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar">
                                {sections.length === 0 ? (
                                    <div className="text-center text-slate-500 text-[10px] py-4">
                                        No stages available
                                    </div>
                                ) : (
                                    sections.map((section, sectionIdx) => {
                                        const legStages = section.stages || [];
                                        const isExpanded = selectedSectionName === section.section;
                                        const hasCurrentStage = legStages.some(st => st.name === stageName);
                                        const currentStageInLeg = legStages.find(st => st.name === stageName);

                                        return (
                                            <div key={section.section || sectionIdx} className="border border-slate-200 rounded-xl overflow-hidden bg-white/40">
                                                {/* Leg Header */}
                                                <div
                                                    className="flex items-center justify-between p-2.5 cursor-pointer hover:bg-white/60 transition-colors"
                                                    onClick={() => {
                                                        if (onSectionSelect) {
                                                            onSectionSelect(section);
                                                        }
                                                    }}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <span className="material-symbols-outlined text-slate-400 text-base transition-transform" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                                            expand_more
                                                        </span>
                                                        <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                                                            {section.section || `Leg ${sectionIdx + 1}`}
                                                        </span>
                                                    </div>
                                                    <span className="text-[10px] font-semibold text-white bg-blue-500 px-2 py-0.5 rounded-full">
                                                        {legStages.length} STAGE{legStages.length !== 1 ? 'S' : ''}
                                                    </span>
                                                </div>

                                                {/* Current Stage Display (when collapsed and has current stage) */}
                                                {!isExpanded && hasCurrentStage && currentStageInLeg && (
                                                    <div className="px-2.5 pb-2.5">
                                                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-2.5 text-white shadow-md">
                                                            <div className="text-[8px] font-bold uppercase tracking-wider opacity-90 mb-1">Current Stage</div>
                                                            <div className="text-sm font-bold">{currentStageInLeg.name}</div>
                                                            <div className="text-[10px] font-mono mt-1 opacity-90">
                                                                {(() => {
                                                                    const stageObj = stages.find(s => s.name === currentStageInLeg.name);
                                                                    const length = stageObj?.routeIndex?.cum
                                                                        ? (stageObj.routeIndex.cum[stageObj.routeIndex.cum.length - 1] / 1000).toFixed(3)
                                                                        : '0.000';
                                                                    return `${length} km`;
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Expanded Stages List */}
                                                {isExpanded && (
                                                    <div className="px-2 pb-2 space-y-1">
                                                        {legStages.map((st, stIdx) => {
                                                            const isCurrentStage = st.name === stageName;
                                                            const stageObj = stages.find(s => s.name === st.name);
                                                            const length = stageObj?.routeIndex?.cum
                                                                ? (stageObj.routeIndex.cum[stageObj.routeIndex.cum.length - 1] / 1000).toFixed(3)
                                                                : '0.000';

                                                            return (
                                                                <div
                                                                    key={st.id || stIdx}
                                                                    className={`p-2 rounded-lg cursor-pointer transition-all ${isCurrentStage
                                                                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md'
                                                                        : 'bg-white/60 hover:bg-white border border-slate-200 hover:border-blue-300'
                                                                        }`}
                                                                    onClick={() => {
                                                                        setStageName(st.name);
                                                                        if (onStageChange) {
                                                                            onStageChange(st.name);
                                                                        }
                                                                        if (onStageSelect) {
                                                                            onStageSelect(st, section);
                                                                        }
                                                                    }}
                                                                >
                                                                    <div className="flex items-center justify-between">
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className={`text-[10px] font-bold uppercase tracking-wider mb-0.5 ${isCurrentStage ? 'text-white/90' : 'text-slate-400'
                                                                                }`}>
                                                                                {isCurrentStage ? 'Current Stage' : `Stage ${stIdx + 1}`}
                                                                            </div>
                                                                            <div className={`text-xs font-bold truncate ${isCurrentStage ? 'text-white' : 'text-slate-700'
                                                                                }`}>
                                                                                {st.name}
                                                                            </div>
                                                                        </div>
                                                                        <div className={`text-[10px] font-mono font-semibold ml-2 ${isCurrentStage ? 'text-white/90' : 'text-blue-600'
                                                                            }`}>
                                                                            {length} km
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Progress Panel */}
            <div className={`glass-panel rounded-2xl shadow-xl flex flex-col overflow-hidden pointer-events-auto transition-all ${isProgressVisible ? 'flex-1 min-h-0' : 'shrink-0 h-auto'}`}>
                <header className="px-3 py-2.5 flex items-center justify-between border-b border-black/5 bg-white/20">
                    <div className="flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-primary text-lg">monitoring</span>
                        <h2 className="font-extrabold text-slate-800 uppercase tracking-tight text-[11px]">
                            Progress
                        </h2>
                    </div>
                    <span
                        className="material-symbols-outlined text-slate-400 text-base cursor-pointer"
                        onClick={() => setIsProgressVisible(!isProgressVisible)}
                    >
                        {isProgressVisible ? 'expand_less' : 'expand_more'}
                    </span>
                </header>

                {isProgressVisible && (
                    <>
                        {/* Tab Navigation */}
                        <div className="flex border-b border-black/5">
                            <button
                                onClick={() => setActiveProgressTab('analytics')}
                                className={`flex-1 py-2 text-xs flex items-center justify-center gap-2 border-b-2 transition-colors ${activeProgressTab === 'analytics'
                                    ? 'border-primary text-primary font-bold'
                                    : 'border-transparent text-slate-500 hover:text-slate-700'
                                    }`}
                            >
                                <span className="material-symbols-outlined text-base">analytics</span> ANALYTICS
                            </button>
                            <button
                                onClick={() => setActiveProgressTab('timeControl')}
                                className={`flex-1 py-2 text-xs flex items-center justify-center gap-2 border-b-2 transition-colors ${activeProgressTab === 'timeControl'
                                    ? 'border-primary text-primary font-bold'
                                    : 'border-transparent text-slate-500 hover:text-slate-700'
                                    }`}
                            >
                                <span className="material-symbols-outlined text-base">schedule</span> TIME
                            </button>
                            <button
                                onClick={() => setActiveProgressTab('results')}
                                className={`flex-1 py-2 text-xs flex items-center justify-center gap-2 border-b-2 transition-colors ${activeProgressTab === 'results'
                                    ? 'border-primary text-primary font-bold'
                                    : 'border-transparent text-slate-500 hover:text-slate-700'
                                    }`}
                            >
                                <span className="material-symbols-outlined text-base">emoji_events</span> RESULTS
                            </button>
                        </div>

                        {/* Analytics Tab Content */}
                        {activeProgressTab === 'analytics' && (
                            <>
                                {/* Stage Selector */}
                                <div className="p-2 border-b border-black/5">
                                    <div className="relative">
                                        <select
                                            className="w-full bg-white/40 border border-slate-300 rounded-lg py-1 px-3 text-[11px] text-slate-700 font-bold appearance-none outline-none"
                                            style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', backgroundImage: 'none' }}
                                            value={stageName}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setStageName(value);
                                                if (onStageChange) {
                                                    onStageChange(value);
                                                }
                                                // Find the stage and trigger selection
                                                const stage = allStages.find(s => s.name === value);
                                                if (stage && onStageSelect) {
                                                    const section = sections.find(sec => sec.section === stage.section);
                                                    if (section) {
                                                        onStageSelect(stage, section);
                                                    }
                                                }
                                            }}
                                        >
                                            <option value="">Select a stage</option>
                                            {allStages.map((st) => (
                                                <option key={st.id} value={st.name}>
                                                    {st.name}
                                                </option>
                                            ))}
                                        </select>
                                        <span className="material-symbols-outlined absolute right-2 top-1 text-slate-400 text-sm pointer-events-none">
                                            expand_more
                                        </span>
                                    </div>
                                </div>

                                {/* Progress List */}
                                <div className="flex-1 overflow-y-auto custom-scrollbar px-3 py-2 space-y-[2px]">
                                    {!stageName ? (
                                        <div className="text-center text-slate-500 text-[10px] py-4">
                                            Select a stage to view progress
                                        </div>
                                    ) : animatedRows.length === 0 ? (
                                        <div className="text-center text-slate-500 text-[10px] py-4">
                                            No devices on this stage
                                        </div>
                                    ) : (
                                        animatedRows.map(({ rowId, data: d, phase }) => {
                                            const kmValue = Number.parseFloat(d.distanceOnPolyline);
                                            const validKm = Number.isFinite(kmValue) ? kmValue : 0;
                                            const pct = stageLengthKm ? (validKm / stageLengthKm) * 100 : 0;
                                            const normalizedPct = Math.max(0, Math.min(pct, 100));
                                            const id = d.deviceId ?? d.id;
                                            const name = d.deviceName ? d.deviceName.split('-')[0].trim() : id;
                                            const phaseClass = phase === 'entering' ? 'progress-row-entering' : phase === 'leaving' ? 'progress-row-leaving' : '';

                                            // Bar always uses model color
                                            const model = d.deviceModel || d.model;
                                            const barColor = modelColors[model] || defaultColor;

                                            // Row state highlighting
                                            const isSos = d.sos === true || d.isSosActive === true;
                                            let rowBg = '';
                                            let dotColor = null;
                                            if (isSos) {
                                                rowBg = 'bg-red-50';
                                                dotColor = '#dc2626';
                                            }

                                            return (
                                                <div
                                                    key={rowId}
                                                    className={`flex items-center gap-2 group h-6 cursor-pointer hover:bg-slate-100/50 rounded px-1 transition-colors ${phaseClass} ${rowBg}`}
                                                    onClick={() => {
                                                        if (onDeviceClick && phase !== 'leaving') {
                                                            const lat = Number(d.latitude);
                                                            const lon = Number(d.longitude);
                                                            if (Number.isFinite(lat) && Number.isFinite(lon)) {
                                                                onDeviceClick(lat, lon, id);
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <span className="min-w-[50px] max-w-[80px] text-[10px] font-black text-slate-700 shrink-0 truncate mr-2" title={name}>
                                                        {name}
                                                    </span>
                                                    <div className="flex-1 h-2 bg-slate-200/50 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full transition-all duration-300`}
                                                            style={{ width: `${normalizedPct}%`, backgroundColor: barColor }}
                                                        />
                                                    </div>
                                                    {dotColor && (
                                                        <span
                                                            className="w-2 h-2 rounded-full shrink-0 animate-pulse"
                                                            style={{ backgroundColor: dotColor }}
                                                        />
                                                    )}
                                                    <span className="w-12 text-right text-[10px] font-mono font-bold text-slate-600 shrink-0">
                                                        {Number.isFinite(kmValue) ? `${kmValue.toFixed(2)} km` : '—'}
                                                    </span>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </>
                        )}

                        {/* Time Control Tab Content */}
                        {activeProgressTab === 'timeControl' && (
                            <>
                                <div className="p-3 space-y-2">
                                    {/* Rally selector - inline layout */}
                                    <div className="flex items-center gap-2">
                                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">Rally:</label>
                                        <div className="relative flex-1">
                                            <select
                                                className="w-full bg-white/40 border-none rounded-lg py-1.5 pl-3 pr-8 text-xs text-slate-800 font-bold focus:ring-1 focus:ring-primary shadow-sm appearance-none cursor-pointer"
                                                style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', backgroundImage: 'none' }}
                                                value={selectedRally}
                                                onChange={(e) => setSelectedRally(e.target.value)}
                                                disabled={rallyLoading || rallyOptions.length === 0}
                                            >
                                                {rallyLoading ? (
                                                    <option>Loading...</option>
                                                ) : rallyError ? (
                                                    <option>Error loading rallies</option>
                                                ) : rallyOptions.length === 0 ? (
                                                    <option>No rallies available</option>
                                                ) : (
                                                    rallyOptions.map((rally, idx) => (
                                                        <option key={idx} value={getOptionValue(rally)}>
                                                            {getOptionLabel(rally)}
                                                        </option>
                                                    ))
                                                )}
                                            </select >
                                            <span className="material-symbols-outlined absolute right-2 top-1.5 text-slate-500 text-lg pointer-events-none">expand_more</span>
                                        </div >
                                    </div >
                                    {/* Control Point selector - inline layout */}
                                    < div className="flex items-center gap-2" >
                                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">Point:</label>
                                        <div className="relative flex-1">
                                            <select
                                                className="w-full bg-white/40 border-none rounded-lg py-1.5 pl-3 pr-8 text-xs text-slate-800 font-bold focus:ring-1 focus:ring-primary shadow-sm appearance-none cursor-pointer"
                                                style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', backgroundImage: 'none' }}
                                                value={selectedRallyPoint}
                                                onChange={(e) => setSelectedRallyPoint(e.target.value)}
                                                disabled={pointsLoading || rallyPoints.length === 0}
                                            >
                                                {pointsLoading ? (
                                                    <option>Loading...</option>
                                                ) : pointsError ? (
                                                    <option>Error loading points</option>
                                                ) : rallyPoints.length === 0 ? (
                                                    <option>No control points</option>
                                                ) : (
                                                    rallyPoints.map((point, idx) => (
                                                        <option key={idx} value={getOptionValue(point)}>
                                                            {getOptionLabel(point)}
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                            <span className="material-symbols-outlined absolute right-2 top-1.5 text-slate-500 text-lg pointer-events-none">expand_more</span>
                                        </div>
                                    </div >
                                </div >
                                <div className="flex-1 overflow-y-auto custom-scrollbar">
                                    {timesLoading ? (
                                        <div className="text-center text-slate-500 text-xs py-4">Loading times...</div>
                                    ) : timesError ? (
                                        <div className="text-center text-rose-500 text-xs py-4">{timesError}</div>
                                    ) : normalizedPointTimes.length === 0 ? (
                                        <div className="text-center text-slate-500 text-xs py-4">No time data available</div>
                                    ) : (
                                        <table className="w-full text-left border-collapse">
                                            <thead className="sticky top-0 bg-white/60 backdrop-blur-md z-10 border-b border-black/5">
                                                <tr>
                                                    <th className="pl-5 pr-2 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Number</th>
                                                    <th className="px-5 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest text-right">Time</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-black/5">
                                                {normalizedPointTimes.map((entry, idx) => (
                                                    <tr key={`${entry.number}-${idx}`} className="hover:bg-white/40">
                                                        <td className="pl-5 pr-2 py-1.5 text-sm font-bold text-slate-800">{entry.number}</td>
                                                        <td className="px-5 py-1.5 text-sm font-mono font-medium text-slate-600 text-right">{entry.time}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Results Tab Content */}
                        {
                            activeProgressTab === 'results' && (
                                <>
                                    <div className="p-3 border-b border-black/5">
                                        {/* API Stage Selector */}
                                        <div className="relative">
                                            <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1 tracking-wider">Select Stage</label>
                                            <select
                                                className="w-full bg-white/50 border border-slate-200 rounded-lg py-1.5 px-2.5 text-xs text-slate-800 font-semibold appearance-none focus:ring-2 focus:ring-primary/20 outline-none"
                                                style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', backgroundImage: 'none' }}
                                                value={selectedResultStageId}
                                                onChange={(e) => {
                                                    setSelectedResultStageId(e.target.value);
                                                    setResultsManuallySelected(true);
                                                }}
                                                disabled={!Array.isArray(resultStages) || resultStages.length === 0}
                                            >
                                                <option value="">-- Select Stage --</option>
                                                {resultStages.map((s) => (
                                                    <option key={s.ID} value={s.ID}>
                                                        {s.Name}
                                                    </option>
                                                ))}
                                            </select>
                                            <span className="material-symbols-outlined absolute right-2 top-6 text-slate-400 text-lg pointer-events-none">expand_more</span>
                                        </div>
                                    </div>
                                    <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-2">
                                        {resultsLoading ? (
                                            <div className="text-center text-slate-500 text-[10px] py-4">
                                                Loading results...
                                            </div>
                                        ) : resultsError ? (
                                            <div className="text-center text-rose-500 text-[10px] py-4">
                                                {resultsError}
                                            </div>
                                        ) : !selectedResultStageId ? (
                                            <div className="text-center text-slate-500 text-[10px] py-4">
                                                Select a stage to view results
                                            </div>
                                        ) : stageResults.length === 0 ? (
                                            <div className="text-center text-slate-500 text-[10px] py-4">
                                                No results found for this stage
                                            </div>
                                        ) : (
                                            stageResults.map((result) => {
                                                // Map API fields to UI
                                                const displayStart = result.StartTime || '—';
                                                const displayFinish = result.FinishTime || 'Ongoing';
                                                const displayResult = result.ResultTime || '—';
                                                const status = result.FinishTime ? 'finished' : 'ongoing';
                                                const position = result.Position || result.PositionNumber || '-';

                                                return (
                                                    <div
                                                        key={result.ID || result.PositionNumber}
                                                        className="bg-white/50 border border-white/40 rounded-lg p-1.5 flex items-center gap-3 hover:bg-white/70 transition-colors"
                                                    >
                                                        <div className="text-base font-black text-slate-800 w-10">#{result.PositionNumber}</div>

                                                        <div className="flex-1 flex items-center justify-between text-[10px]">
                                                            <div className="flex flex-col">
                                                                <div className="flex items-center gap-1">
                                                                    <span className="text-slate-400 font-bold uppercase text-[8px]">Start:</span>
                                                                    <span className="font-mono text-slate-600">{displayStart}</span>
                                                                </div>

                                                                {status === 'ongoing' ? (
                                                                    <div className="flex items-center gap-1">
                                                                        <span className="text-slate-400 font-bold uppercase text-[8px]">Status:</span>
                                                                        <span className="font-mono text-amber-500 font-bold italic">Ongoing</span>
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex items-center gap-1">
                                                                        <span className="text-slate-400 font-bold uppercase text-[8px]">Result:</span>
                                                                        <span className="font-mono text-emerald-500 font-bold">{displayResult}</span>
                                                                        <span className="text-slate-400 text-[8px] font-mono ml-1">({displayFinish})</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <span className={`material-symbols-outlined text-base ${status === 'ongoing'
                                                            ? 'text-amber-500'
                                                            : 'text-emerald-500'
                                                            }`}>
                                                            {status === 'ongoing' ? 'schedule' : 'check_circle'}
                                                        </span>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </>
                            )
                        }
                    </>
                )}
            </div >

            {/* Red Flag Button — hidden for readonly users */}
            {!isReadOnly && (
                <div className="mt-2 text-center pb-2 pointer-events-auto">
                    <button
                        onClick={() => setIsRedFlagModalOpen(true)}
                        className="w-full bg-rose-600 hover:bg-rose-700 text-white font-bold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] active:scale-[0.98]"
                    >
                        <span className="material-symbols-outlined">flag</span>
                        RED FLAG
                    </button>
                </div>
            )}

            {/* Red Flag Modal */}
            <RedFlagModal
                isOpen={isRedFlagModalOpen}
                onClose={() => setIsRedFlagModalOpen(false)}
                stages={allStages}
                devices={devices}
                initialStageName={stageName}
            />

            <SetupPopup
                isOpen={isSetupPopupOpen}
                onClose={() => setIsSetupPopupOpen(false)}
                currentMapId={currentMapId}
                onMapSelect={(id) => {
                    if (onMapChange) onMapChange(id);
                }}
            />
        </aside >
    );
}

export default CompactSidePanel;
